import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useWatch, type UseFormReturn } from 'react-hook-form';

import type { LayersQuery } from '@/graphql/graphql';
import { getResourcesEndpoint } from '@/core/resources';
import { Checkbox } from '@/components/ui/checkbox';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type LayerData = LayersQuery['layers'][number];
/**
 * The form-field PREFIX this picker owns: it reads and writes
 * `${prefix}LayerId`, `${prefix}SurfaceIds` and `${prefix}PcMethod`.
 *
 * The two template members are the layer-stack dialog's per-level pickers
 * (`levels.0.designLayerId`, `levels.0.asBuiltLayerId`, …). They are a
 * widening of this union rather than a hand-rolled `<Select>` on purpose: a
 * bare select supplies neither `pcMethod` nor `surfaceIds`, and dropping
 * those silently falls back to the whole file at `min`.
 */
export type SurfaceSide =
  | 'top'
  | 'bottom'
  | 'expectUpper'
  | 'expectLower'
  | 'inputs.top'
  | 'inputs.bottom'
  | `levels.${number}.design`
  | `levels.${number}.asBuilt`;

export const PC_METHODS = ['min', 'mean', 'median', 'p05', 'max'] as const;

interface Props {
  form: UseFormReturn<any>;
  prefix: SurfaceSide;
  layers: LayerData[];
  label: string;
  /**
   * The point-cloud sampling field, for a caller that needs it to say more
   * (Compare surfaces, spec §12.6 / §12.8). Omitted: shown for every point
   * cloud with its plain label, as before.
   */
  sampling?: {
    /** False hides the field even for a point cloud (e.g. the method does
     *  not apply to the results the user picked). */
    visible: boolean;
    label?: ReactNode;
    help?: ReactNode;
    /** A note under the field (e.g. a re-run's legacy method). */
    notice?: ReactNode;
  };
}

const isLandXml = (l: LayerData | undefined) => l?.type === 'MODEL' && (l.metadata as any)?.type === 'LANDXML';

/**
 * A short, translated name for a layer's kind ("Point cloud", "LandXML", …)
 * instead of the raw enum (`POINTCLOUD`) the options used to print. An
 * unknown kind falls back to the raw value rather than to nothing.
 */
const LAYER_TYPE_KEYS = {
  pointcloud: 'analysisConfig.layerTypes.pointcloud',
  vector: 'analysisConfig.layerTypes.vector',
  landxml: 'analysisConfig.layerTypes.landxml',
  ifc: 'analysisConfig.layerTypes.ifc',
  dxf: 'analysisConfig.layerTypes.dxf',
} as const;

function layerTypeKey(l: LayerData): (typeof LAYER_TYPE_KEYS)[keyof typeof LAYER_TYPE_KEYS] | null {
  const sub = ((l.metadata as { type?: string } | null)?.type ?? '').toUpperCase();
  if (l.type === 'POINTCLOUD') return LAYER_TYPE_KEYS.pointcloud;
  if (l.type === 'VECTOR') return LAYER_TYPE_KEYS.vector;
  if (l.type !== 'MODEL') return null;
  if (sub === 'LANDXML') return LAYER_TYPE_KEYS.landxml;
  if (sub === 'IFC' || sub === 'FRAGMENT') return LAYER_TYPE_KEYS.ifc;
  if (sub === 'DXF') return LAYER_TYPE_KEYS.dxf;
  return null;
}

/**
 * Fetch the <Surface name=…> list of a LandXML layer (cheap: only the names).
 *
 * Exported for `SectionOverlaysPopover`, which asks the same question for the
 * same reason — an overlay on a LandXML file has to name ONE surface, or the
 * worker cuts every surface in the file and draws their union.
 *
 * Returns the fetch STATE and not just the names, because `[]` has three very
 * different meanings and a caller that has to gate a button on it cannot tell
 * them apart: not started, in flight, and "this file has no surfaces in it".
 * This picker only ever renders the list, so it ignores the extra two fields;
 * the popover disables Add on them.
 */
export function useLandXmlSurfaceNames(layer: LayerData | undefined): {
  names: string[];
  loading: boolean;
  error: boolean;
} {
  const [state, setState] = useState<{ names: string[]; loading: boolean; error: boolean }>({
    names: [],
    loading: false,
    error: false,
  });
  useEffect(() => {
    if (!isLandXml(layer) || !layer?.root) {
      setState({ names: [], loading: false, error: false });
      return;
    }
    setState({ names: [], loading: true, error: false });
    const ctrl = new AbortController();
    fetch(`${getResourcesEndpoint()}/${layer.root}`, { signal: ctrl.signal, credentials: 'include' })
      .then((r) => {
        // A signed-URL 403 or a 5xx returns a BODY, and parsing it as XML
        // yields zero <Surface> elements — indistinguishable from a valid
        // file with no surfaces unless the status is checked here.
        if (!r.ok) throw new Error(`${r.status}`);
        return r.text();
      })
      .then((txt) => {
        const doc = new DOMParser().parseFromString(txt, 'application/xml');
        setState({
          names: Array.from(doc.getElementsByTagName('Surface')).map((s, i) => s.getAttribute('name') ?? String(i)),
          loading: false,
          error: false,
        });
      })
      .catch((err: unknown) => {
        // An abort is this effect cleaning up after itself, not a failure:
        // the next run has already set `loading` and would be overwritten.
        if (ctrl.signal.aborted || (err as { name?: string })?.name === 'AbortError') return;
        setState({ names: [], loading: false, error: true });
      });
    return () => ctrl.abort();
  }, [layer?.id, layer?.root]);
  return state;
}

export function SurfacePicker({ form, prefix, layers, label, sampling }: Props) {
  const { t } = useTranslation('project');
  const typeLabel = (l: LayerData) => {
    const key = layerTypeKey(l);
    return key ? t(key) : ((l.metadata as { type?: string } | null)?.type ?? l.type);
  };
  // `useWatch`, NOT `form.watch(...)`. This component receives `form` as a
  // PROP rather than calling `useForm()` itself, and in that position
  // `form.watch()` creates no subscription: it returns whatever the field held
  // when this component first mounted and never updates again. Every read below
  // is derived from `layerId`, so the stale value was not cosmetic —
  //   * `layer?.type === 'POINTCLOUD'` kept the point-cloud sampling control on
  //     screen for a LandXML pick (and hid it for a point-cloud one),
  //   * `useLandXmlSurfaceNames(layer)` listed the PREVIOUS layer's
  //     `<Surface name=…>` entries, so per-surface selection wrote surface ids
  //     that do not exist in the file actually being analysed, and
  //   * the trigger's `title` — the only way to read a name the truncation
  //     hides — named a different layer than the one selected.
  // Confirmed in a browser against a real project on 2026-09-05: the trigger
  // read `26-OB-0402-BP1-BP4_PART5.xml (LANDXML)` while this component still
  // believed the layer was `20260807 PointCloud.las (POINTCLOUD)`.
  // `useWatch`/`useFormState` subscribe via `useSyncExternalStore`, so the
  // child re-renders. With the React Compiler on, the stale read fails
  // silently in the browser and still passes a unit test that only mounts the
  // component once — hence the switching test in
  // `__tests__/SurfacePicker.dom.test.tsx`.
  const layerId = useWatch({ control: form.control, name: `${prefix}LayerId` }) as string;
  const layer = layers.find((l) => l.id === layerId);
  const { names: surfaceNames, error: surfacesError } = useLandXmlSurfaceNames(layer);
  const selected = (useWatch({ control: form.control, name: `${prefix}SurfaceIds` }) as string[] | undefined) ?? [];
  // Unreadable file: the run will use every surface (no ids sent), so a pick
  // carried in from before (a re-run, the previous file) is dropped to make
  // the note below true rather than send ids nobody can see.
  const hasPick = selected.length > 0;
  useEffect(() => {
    if (surfacesError && hasPick) form.setValue(`${prefix}SurfaceIds`, []);
  }, [surfacesError, hasPick, form, prefix]);
  const headingId = useId();

  return (
    // A named group: a screen reader entering it hears the role ("Design"),
    // and the layer combobox itself reads "Design: Layer" (the sr-only role
    // prefix below), so two pickers' "Layer" fields are never ambiguous.
    <div
      role='group'
      aria-labelledby={headingId}
      className='min-w-0 rounded-md border border-slate-200 p-3 dark:border-stone-700 flex flex-col gap-3'>
      <div id={headingId} className='text-sm font-semibold'>
        {label}
      </div>
      <FormField
        control={form.control}
        name={`${prefix}LayerId`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>
              <span className='sr-only'>{label}: </span>
              {t('surfaceDifference.layer')}
            </FormLabel>
            <Select
              onValueChange={(v) => {
                field.onChange(v);
                form.setValue(`${prefix}SurfaceIds`, []);
              }}
              value={field.value ?? undefined}>
              <FormControl>
                {/* `w-full min-w-0`, not the shadcn default `w-fit`: `w-fit`
                    lets the trigger grow to whatever width the selected
                    layer's name needs, which defeats the `line-clamp-1`
                    truncation `SelectTrigger` already applies to its
                    `SelectValue` slot (that class only clips once the box
                    has a bound to clip AT). `title` is the hover fallback
                    for the name the truncation hides. */}
                <SelectTrigger
                  className='w-full min-w-0'
                  title={layer ? `${layer.name} (${typeLabel(layer)})` : undefined}>
                  {/* The expectation surfaces (fix round 2) start at a null
                      layer id, unlike `top`/`bottom` which are always
                      pre-seeded — without a placeholder the trigger renders
                      completely empty, with no label and no prompt. Inert
                      for every existing caller, whose value is never empty. */}
                  <SelectValue placeholder={t('surfaceDifference.chooseLayer')} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {layers.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    {l.name} ({typeLabel(l)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      {surfaceNames.length > 1 && (
        <div className='flex flex-col gap-1'>
          <div className='text-xs font-medium'>{t('surfaceDifference.surfaces')}</div>
          <label className='flex items-center gap-2 text-sm'>
            <Checkbox
              checked={selected.length === 0}
              onCheckedChange={() => form.setValue(`${prefix}SurfaceIds`, [])}
            />
            {t('surfaceDifference.allSurfaces')}
          </label>
          {surfaceNames.map((n) => (
            // `min-w-0` on the row AND `truncate` on the name itself: a LandXML
            // `<Surface name=…>` is free-text and can be as long as a layer
            // name. `title` carries the untruncated name.
            <label key={n} className='flex min-w-0 items-center gap-2 text-sm' title={n}>
              <Checkbox
                checked={selected.includes(n)}
                onCheckedChange={(c) =>
                  form.setValue(`${prefix}SurfaceIds`, c ? [...selected, n] : selected.filter((s) => s !== n))
                }
              />
              <span className='min-w-0 truncate'>{n}</span>
            </label>
          ))}
        </div>
      )}
      {/* Always mounted, so the note is announced when it appears (a live
          region announces changes, not insertions). Empty, it cancels the gap. */}
      <p
        role='status'
        data-testid={`${prefix}-surfaces-load-failed`}
        className='text-xs text-amber-700 empty:-mt-3 dark:text-amber-400'>
        {surfacesError ? t('surfaceDifference.surfacesLoadFailed') : null}
      </p>
      {layer?.type === 'POINTCLOUD' && (sampling?.visible ?? true) && (
        <FormField
          control={form.control}
          name={`${prefix}PcMethod`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{sampling?.label ?? t('surfaceDifference.pcMethod')}</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger className='w-full min-w-0'>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {PC_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {t(`surfaceDifference.pcMethods.${m}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* One description, so `FormControl`'s `aria-describedby` on the
                  trigger covers the help AND the notice. */}
              {(sampling?.help || sampling?.notice) && (
                <FormDescription className='flex flex-col gap-1 text-xs'>
                  {sampling.help && <span>{sampling.help}</span>}
                  {sampling.notice && (
                    <span data-slot='sampling-notice' className='text-amber-700 dark:text-amber-400'>
                      {sampling.notice}
                    </span>
                  )}
                </FormDescription>
              )}
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
